import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Loading } from '@/components/Loading';
import { PageHeader } from '@/components/PageHeader';
import { useStatisticsStore } from '@/stores/useStatisticsStore';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaChartBar,
  FaChartLine,
  FaCreditCard,
  FaEye,
  FaGlobe,
  FaUsers,
} from 'react-icons/fa';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const formatMonth = (month: string) => month.substring(0, 3);

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, when: 'beforeChildren' },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

const StatCard = ({ title, value, subtitle, icon, color }: StatCardProps) => {
  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card className="h-full border-gray-100 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-600">{title}</h3>
              <div className="mt-2 text-3xl font-bold text-gray-900">
                {value.toLocaleString()}
              </div>
              {subtitle && (
                <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
              )}
            </div>
            <div className={`rounded-full p-3 ${color}`}>{icon}</div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export const AdminStatistics = () => {
  const { t } = useTranslation('adminStatistics');
  const { adminStatistics, isLoading, fetchAdminStatistics } =
    useStatisticsStore();
  const [activeChart, setActiveChart] = useState<'line' | 'bar'>('line');

  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
          <p className="font-semibold text-gray-900">{label}</p>
          <p className="text-linko-light">
            {t('chart.total')}: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    fetchAdminStatistics();
  }, [fetchAdminStatistics]);

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!adminStatistics) {
    return null;
  }

  return (
    <div className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between lg:gap-0"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <PageHeader title={t('title')} />
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6 px-4"
      >
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <StatCard
            title={t('cards.totalUsers')}
            value={adminStatistics.totalUsersCount}
            icon={<FaUsers className="size-6 text-white" />}
            color="bg-linko-light"
          />
          <StatCard
            title={t('cards.onlineUsers')}
            value={adminStatistics.onlineUsersCount}
            icon={<FaGlobe className="size-6 text-white" />}
            color="bg-green-500"
          />
          <StatCard
            title={t('cards.cards.title')}
            value={adminStatistics.totalCardsCount.active}
            subtitle={`${adminStatistics.totalCardsCount.inactive} ${t('cards.cards.inactive')}`}
            icon={<FaCreditCard className="size-6 text-white" />}
            color="bg-purple-500"
          />
          <StatCard
            title={t('cards.accessories.title')}
            value={adminStatistics.totalAccessoriesCount.linked}
            subtitle={`${adminStatistics.totalAccessoriesCount.unlinked} ${t('cards.accessories.unlinked')}, ${adminStatistics.totalAccessoriesCount.disabled} ${t('cards.accessories.disabled')}`}
            icon={<FaCreditCard className="size-6 text-white" />}
            color="bg-orange-500"
          />
          <StatCard
            title={t('cards.totalScans')}
            value={adminStatistics.totalScansCount}
            icon={<FaEye className="size-6 text-white" />}
            color="bg-indigo-500"
          />
          <StatCard
            title={t('cards.profilesScanned')}
            value={adminStatistics.totalProfilesScanned}
            icon={<FaEye className="size-6 text-white" />}
            color="bg-pink-500"
          />
        </div>

        {/* Chart Section */}
        <motion.div variants={itemVariants}>
          <Card className="border-gray-100 bg-white shadow-sm">
            <div className="p-6">
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('chart.title')}
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setActiveChart('line')}
                    className={`flex items-center gap-2 ${
                      activeChart === 'line'
                        ? 'bg-linko-light text-white'
                        : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FaChartLine className="size-4" />
                    {t('chart.buttons.line')}
                  </Button>
                  <Button
                    onClick={() => setActiveChart('bar')}
                    className={`flex items-center gap-2 ${
                      activeChart === 'bar'
                        ? 'bg-linko-light text-white'
                        : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FaChartBar className="size-4" />
                    {t('chart.buttons.bar')}
                  </Button>
                </div>
              </div>

              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  {activeChart === 'line' ? (
                    <LineChart
                      data={adminStatistics.userInscriptionsEvolutionByMonth}
                      margin={{ left: -15, right: 15, top: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="month"
                        tickFormatter={formatMonth}
                        stroke="#64748b"
                        fontSize={12}
                        axisLine={{ stroke: '#e2e8f0' }}
                        tick={{ fill: '#64748b' }}
                      />
                      <YAxis
                        stroke="#64748b"
                        fontSize={12}
                        axisLine={{ stroke: '#e2e8f0' }}
                        tick={{ fill: '#64748b' }}
                        width={35}
                        tickMargin={5}
                      />
                      <Tooltip
                        content={<CustomTooltip />}
                        contentStyle={{
                          borderRadius: '0.5rem',
                          border: '1px solid #e2e8f0',
                          boxShadow: 'none',
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#0ea5e9"
                        strokeWidth={3}
                        dot={{ fill: '#0ea5e9', r: 5 }}
                        activeDot={{ r: 7, fill: '#0284c7' }}
                      />
                    </LineChart>
                  ) : (
                    <BarChart
                      data={adminStatistics.userInscriptionsEvolutionByMonth}
                      margin={{ left: -15, right: 15, top: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="month"
                        tickFormatter={formatMonth}
                        stroke="#64748b"
                        fontSize={12}
                        axisLine={{ stroke: '#e2e8f0' }}
                        tick={{ fill: '#64748b' }}
                      />
                      <YAxis
                        stroke="#64748b"
                        fontSize={12}
                        axisLine={{ stroke: '#e2e8f0' }}
                        tick={{ fill: '#64748b' }}
                        width={35}
                        tickMargin={5}
                      />
                      <Tooltip
                        content={<CustomTooltip />}
                        contentStyle={{
                          borderRadius: '0.5rem',
                          border: '1px solid #e2e8f0',
                          boxShadow: 'none',
                        }}
                      />
                      <Bar
                        dataKey="count"
                        fill="#0ea5e9"
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </div>
  );
};
